import { useEffect, useState, useCallback, Fragment } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, DollarSign, UsersRound, Zap, Download, CheckCircle2, ChevronDown, ChevronRight, Printer, Trash2, History, User, Circle, Play, Square, Plus, FileText, Eye } from "lucide-react";

const fmtMin = (m) => { const h = Math.floor(m / 60); const mm = Math.round(m % 60); return h > 0 ? `${h}h ${mm}m` : `${mm}m`; };
const fmtH = (h) => `${(h || 0).toFixed(2)} h`;
const iso = (d) => d.toISOString().slice(0, 10);

export default function Payroll() {
  const { isAdmin } = useAuth();
  const [through, setThrough] = useState(iso(new Date()));
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [runs, setRuns] = useState([]);
  const [confirmRun, setConfirmRun] = useState(false);
  const [profileUid, setProfileUid] = useState(null);
  const [profile, setProfile] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [runDetail, setRunDetail] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [manual, setManual] = useState({ user_id: "", job_id: "", date: iso(new Date()), hours: "", note: "" });

  const loadPreview = useCallback(() => {
    api.get("/payroll/preview", { params: { through } }).then((r) => setRows(r.data.rows || [])).catch(() => {});
  }, [through]);
  const loadRuns = useCallback(() => { api.get("/payroll/runs").then((r) => setRuns(r.data)).catch(() => {}); }, []);

  useEffect(() => { if (isAdmin) loadPreview(); }, [isAdmin, loadPreview]);
  useEffect(() => { if (isAdmin) { loadRuns(); api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}); } }, [isAdmin, loadRuns]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const fetchProfile = useCallback(() => { if (profileUid) api.get(`/team/${profileUid}/profile`).then((r) => setProfile(r.data)).catch(() => {}); }, [profileUid]);
  useEffect(() => { if (!profileUid) { setProfile(null); return; } fetchProfile(); }, [profileUid, fetchProfile]);
  const clockIn = async (jobId) => { await api.post(`/jobs/${jobId}/crew/${profileUid}/clock-in`); toast.success("Clocked in"); fetchProfile(); loadPreview(); };
  const clockOut = async (jobId) => { await api.post(`/jobs/${jobId}/crew/${profileUid}/clock-out`); toast.success("Clocked out — time logged"); fetchProfile(); loadPreview(); };

  const totalHours = rows.reduce((a, r) => a + r.hours, 0);
  const totalOT = rows.reduce((a, r) => a + (r.ot_hours || 0), 0);
  const totalPay = rows.reduce((a, r) => a + r.pay, 0);

  const exportCSV = () => {
    const header = ["Employee", "Regular Hours", "OT Hours", "Total Hours", "Rate", "Pay"];
    const lines = rows.map((r) => [r.name, r.regular_hours, r.ot_hours, r.hours, r.hourly_rate, r.pay].join(","));
    const csv = [header.join(","), ...lines, ["TOTAL", "", totalOT.toFixed(2), totalHours.toFixed(2), "", totalPay.toFixed(2)].join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payroll_open_${through}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const runPayroll = async () => {
    try {
      await api.post("/payroll/runs", { through });
      toast.success("Payroll run saved — hours locked & marked paid");
      setConfirmRun(false);
      loadPreview(); loadRuns();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addManual = async () => {
    if (!manual.user_id || !manual.job_id || !manual.hours) return toast.error("Pick an employee, a job and enter hours");
    try {
      await api.post("/payroll/manual-entry", { ...manual, hours: Number(manual.hours) });
      toast.success("Time added — it rolls into the open pay period");
      setManualOpen(false);
      setManual({ user_id: "", job_id: "", date: iso(new Date()), hours: "", note: "" });
      loadPreview();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openRun = async (id) => { try { const { data } = await api.get(`/payroll/runs/${id}`); setRunDetail(data); } catch (e) { toast.error("Could not load run"); } };
  const delRun = async (id) => { if (!window.confirm("Delete this payroll run? Its hours will reopen and roll into the next run.")) return; await api.delete(`/payroll/runs/${id}`); toast.success("Run deleted — its hours reopened for the next run"); loadRuns(); loadPreview(); };

  const downloadPdf = async (run) => {
    try {
      const { data } = await api.get(`/payroll/runs/${run.id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      a.download = `payroll_${run.through || "run"}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Could not generate PDF"); }
  };

  const printStub = (r, periodLabel) => {
    const w = window.open("", "_blank");
    const jobRows = (r.days || []).map((j) => `<tr><td>${j.date}${j.manual ? " (manual)" : ""}</td><td style="text-align:right">${j.hours}h</td></tr>`).join("");
    w.document.write(`<html><head><title>Pay Stub — ${r.name}</title><style>
      body{font-family:Arial,sans-serif;padding:40px;color:#14532d}
      h1{font-size:20px;margin:0}h2{font-size:14px;color:#666;margin:4px 0 24px}
      table{width:100%;border-collapse:collapse;margin-top:12px}td,th{padding:8px;border-bottom:1px solid #eee;text-align:left;font-size:13px}
      .tot{font-weight:bold;font-size:18px;color:#14532d;margin-top:20px}
      .box{border:1px solid #ddd;border-radius:8px;padding:16px;margin-top:16px}
    </style></head><body>
      <h1>Neighborhood Lawn Care LLC</h1><h2>Pay Stub — ${periodLabel}</h2>
      <div class="box"><strong>${r.name}</strong><br/>Rate: ${money(r.hourly_rate)}/hr</div>
      <table><tr><th>Regular Hours</th><th>OT Hours (1.5×)</th><th>Total Hours</th></tr>
        <tr><td>${r.regular_hours}h</td><td>${r.ot_hours}h</td><td>${r.hours}h</td></tr></table>
      <h3 style="margin-top:24px;font-size:14px">Jobs Worked</h3>
      <table><tr><th>Date</th><th>Job</th><th>Customer</th><th style="text-align:right">Hours</th></tr>${jobRows || '<tr><td colspan=4>No jobs</td></tr>'}</table>
      <p class="tot">Total Pay: ${money(r.pay)}</p>
    </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Open pay period controls */}
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Pay through</Label><Input type="date" value={through} onChange={(e) => setThrough(e.target.value)} data-testid="payroll-through" className="w-40" /></div>
        <p className="text-xs text-muted-foreground max-w-sm">Shows all unpaid hours up to this date. Running payroll locks them as a static record; any time added later (even for past dates) rolls into the next run.</p>
        <div className="ml-auto flex gap-2">
          {isAdmin && <Button size="sm" variant="outline" onClick={() => setManualOpen(true)} data-testid="add-time-btn"><Plus className="h-4 w-4 mr-1.5" />Add Time</Button>}
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          {isAdmin && <Button size="sm" onClick={() => setConfirmRun(true)} disabled={rows.length === 0} data-testid="run-payroll-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" />Run Payroll</Button>}
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-5"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Employees</span><UsersRound className="h-4 w-4 text-accent" /></div><div className="font-display text-2xl font-black mt-2">{rows.length}</div></Card>
        <Card className="p-5"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Unpaid Hours</span><Clock className="h-4 w-4 text-secondary" /></div><div className="font-display text-2xl font-black mt-2">{fmtH(totalHours)}</div></Card>
        <Card className="p-5"><div className="flex items-center justify-between"><span className="overline text-muted-foreground">Overtime</span><Zap className="h-4 w-4 text-amber-500" /></div><div className="font-display text-2xl font-black mt-2">{fmtH(totalOT)}</div></Card>
        <Card className="p-5 bg-primary text-primary-foreground"><div className="flex items-center justify-between"><span className="overline text-primary-foreground/70">Open Payroll</span><DollarSign className="h-4 w-4" /></div><div className="font-display text-2xl font-black mt-2">{money(totalPay)}</div></Card>
      </div>

      <h3 className="font-semibold text-sm">Open Pay Period — unpaid hours</h3>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Employee</TableHead>
              <TableHead className="text-right">Reg</TableHead>
              <TableHead className="text-right">OT</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Pay</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No unpaid hours. Log time or add a manual entry to build the next payroll.</TableCell></TableRow>
            ) : rows.map((r) => (
              <Fragment key={r.user_id}>
                <TableRow data-testid="payroll-row" className="hover:bg-muted/30 cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [r.user_id]: !e[r.user_id] }))}>
                  <TableCell className="font-semibold flex items-center gap-1.5">{expanded[r.user_id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{r.name}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{r.regular_hours}h</TableCell>
                  <TableCell className="text-right tabular-nums">{r.ot_hours > 0 ? <Badge className="bg-amber-100 text-amber-700">{r.ot_hours}h</Badge> : <span className="text-muted-foreground">0h</span>}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtH(r.hours)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{money(r.hourly_rate)}/hr</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-primary">{money(r.pay)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={(e) => { e.stopPropagation(); setProfileUid(r.user_id); }} data-testid="employee-profile-btn" title="Profile & clock" className="p-1.5 rounded hover:bg-muted"><User className="h-4 w-4 text-muted-foreground" /></button>
                      <button onClick={(e) => { e.stopPropagation(); printStub(r, `open period through ${through}`); }} data-testid="print-stub-btn" title="Pay stub" className="p-1.5 rounded hover:bg-muted"><Printer className="h-4 w-4 text-muted-foreground" /></button>
                    </div>
                  </TableCell>
                </TableRow>
                {expanded[r.user_id] && (
                  <TableRow className="bg-muted/20">
                    <TableCell colSpan={7} className="p-0">
                      <div className="px-6 py-3">
                        {(r.days || []).length === 0 ? <p className="text-xs text-muted-foreground">No day detail.</p> : (
                          <table className="w-full text-sm">
                            <tbody>
                              {r.days.map((j, i) => (
                                <tr key={i} className="border-b border-border/50 last:border-0">
                                  <td className="py-1.5 text-muted-foreground w-28">{j.date}</td>
                                  <td className="py-1.5">{j.manual ? <Badge variant="outline" className="text-[0.6rem]">manual entry</Badge> : "Day shift"}</td>
                                  <td className="py-1.5 text-right tabular-nums">{j.hours}h</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Payroll runs history */}
      {isAdmin && runs.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-semibold flex items-center gap-2 text-sm"><History className="h-4 w-4 text-primary" />Payroll Runs (static records)</h3>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Paid Through</TableHead><TableHead>Employees</TableHead><TableHead className="text-right">Total Paid</TableHead><TableHead>Run On</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.id} data-testid="payroll-run-row" className="hover:bg-muted/30 cursor-pointer" onClick={() => openRun(run.id)}>
                    <TableCell className="font-semibold">{run.through || `${run.start || "—"} → ${run.end || "—"}`}</TableCell>
                    <TableCell>{(run.entries || []).length}</TableCell>
                    <TableCell className="text-right font-semibold text-primary">{money(run.total)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{new Date(run.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => { e.stopPropagation(); openRun(run.id); }} data-testid="view-run-btn" title="View details" className="p-1.5 rounded hover:bg-muted"><Eye className="h-4 w-4 text-muted-foreground" /></button>
                        <button onClick={(e) => { e.stopPropagation(); downloadPdf(run); }} data-testid="download-pdf-btn" title="Download PDF" className="p-1.5 rounded hover:bg-muted"><FileText className="h-4 w-4 text-muted-foreground" /></button>
                        <button onClick={(e) => { e.stopPropagation(); delRun(run.id); }} title="Delete" className="p-1.5 rounded hover:bg-destructive/10"><Trash2 className="h-4 w-4 text-destructive" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Run detail dialog */}
      <Dialog open={!!runDetail} onOpenChange={(o) => !o && setRunDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="run-detail-dialog">
          {runDetail && (
            <>
              <DialogHeader>
                <DialogTitle>Payroll Run — paid through {runDetail.through || `${runDetail.start} → ${runDetail.end}`}</DialogTitle>
                <DialogDescription>Run on {new Date(runDetail.created_at).toLocaleString()}{runDetail.created_by ? ` by ${runDetail.created_by}` : ""} · {(runDetail.entries || []).length} employees · {money(runDetail.total)} total</DialogDescription>
              </DialogHeader>
              <div className="flex justify-end mb-2"><Button size="sm" variant="outline" onClick={() => downloadPdf(runDetail)} data-testid="detail-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />Download PDF</Button></div>
              <Table>
                <TableHeader><TableRow className="bg-muted/50"><TableHead>Employee</TableHead><TableHead className="text-right">Reg</TableHead><TableHead className="text-right">OT</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Pay</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {(runDetail.entries || []).map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-semibold">{e.name}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{e.regular_hours}h</TableCell>
                      <TableCell className="text-right tabular-nums">{e.ot_hours}h</TableCell>
                      <TableCell className="text-right tabular-nums">{e.hours}h</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{money(e.hourly_rate)}/hr</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-primary">{money(e.pay)}</TableCell>
                      <TableCell className="text-right"><button onClick={() => printStub(e, `paid through ${runDetail.through || ""}`)} title="Pay stub" className="p-1.5 rounded hover:bg-muted"><Printer className="h-4 w-4 text-muted-foreground" /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="text-right font-display text-lg font-black text-primary mt-2">Total Paid: {money(runDetail.total)}</div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual time entry dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-entry-dialog">
          <DialogHeader><DialogTitle>Add a time entry</DialogTitle><DialogDescription>Log time an employee worked. It joins the open pay period and will be included in the next payroll run.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Employee</Label>
              <Select value={manual.user_id} onValueChange={(v) => setManual((m) => ({ ...m, user_id: v }))}>
                <SelectTrigger data-testid="manual-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{team.filter((t) => t.active !== false).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date worked</Label><Input type="date" value={manual.date} onChange={(e) => setManual((m) => ({ ...m, date: e.target.value }))} data-testid="manual-date" /></div>
              <div className="space-y-1.5"><Label>Hours</Label><Input type="number" step="0.25" min="0" value={manual.hours} onChange={(e) => setManual((m) => ({ ...m, hours: e.target.value }))} placeholder="e.g. 2.5" data-testid="manual-hours" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note (optional)</Label><Input value={manual.note} onChange={(e) => setManual((m) => ({ ...m, note: e.target.value }))} placeholder="Reason for manual entry" data-testid="manual-note" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button><Button onClick={addManual} data-testid="manual-save-btn">Add Time</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee profile dialog */}
      <Dialog open={!!profileUid} onOpenChange={(o) => !o && setProfileUid(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {profile ? (
            <>
              <DialogHeader><DialogTitle>{profile.user.name}</DialogTitle>
                <DialogDescription className="capitalize">{(profile.user.role || "").replace("_", " ")} · {money(profile.user.hourly_rate)}/hr{profile.user.email ? ` · ${profile.user.email}` : ""}{profile.user.phone ? ` · ${profile.user.phone}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Card className="p-3"><div className="overline text-muted-foreground text-[0.6rem]">Lifetime Hrs</div><div className="font-display text-xl font-black mt-1">{profile.lifetime_hours}h</div></Card>
                  <Card className="p-3"><div className="overline text-muted-foreground text-[0.6rem]">Total Paid</div><div className="font-display text-xl font-black mt-1">{money(profile.total_paid)}</div></Card>
                  <Card className={`p-3 ${profile.active_clock ? "bg-primary/10" : ""}`}><div className="overline text-muted-foreground text-[0.6rem]">Clock</div><div className={`font-display text-xl font-black mt-1 ${profile.active_clock ? "text-primary" : "text-muted-foreground"}`}>{profile.active_clock ? "ON" : "OFF"}</div></Card>
                </div>

                <Card className="p-4" data-testid="profile-clock-panel">
                  {profile.active_clock ? (
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 text-primary font-semibold"><Circle className="h-2.5 w-2.5 fill-current animate-pulse" />On the clock</div>
                        <div className="text-sm text-muted-foreground mt-0.5">{profile.active_clock.job_title} · <span className="tabular-nums font-semibold text-foreground">{fmtMin(Math.max(0, (now - Date.parse(profile.active_clock.clock_in_at)) / 60000))}</span> elapsed</div>
                      </div>
                      <Button onClick={() => clockOut(profile.active_clock.job_id)} data-testid="profile-clock-out-btn" variant="outline"><Square className="h-4 w-4 mr-1.5" />Clock Out</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-sm font-semibold">Off the clock — clock into a job:</div>
                      {profile.today_jobs.length === 0 ? <p className="text-xs text-muted-foreground">No jobs assigned today.</p> : profile.today_jobs.map((tj) => (
                        <div key={tj.job_id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                          <span className="text-sm flex items-center gap-2">{tj.title}<Badge className="capitalize text-[0.6rem]">{(tj.status || "").replace("_", " ")}</Badge></span>
                          <Button size="sm" onClick={() => clockIn(tj.job_id)} data-testid="profile-clock-in-btn"><Play className="h-3.5 w-3.5 mr-1" />Clock In</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <div>
                  <h4 className="font-semibold text-sm flex items-center gap-2 mb-2"><History className="h-4 w-4 text-primary" />Pay History</h4>
                  {profile.run_history.length === 0 ? <p className="text-sm text-muted-foreground">No paid runs yet.</p> : (
                    <Table>
                      <TableHeader><TableRow><TableHead>Period</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Paid</TableHead></TableRow></TableHeader>
                      <TableBody>{profile.run_history.map((rh, i) => (
                        <TableRow key={i}><TableCell className="text-sm">{rh.start || rh.end ? `${rh.start || "—"} → ${rh.end || "—"}` : "—"}</TableCell><TableCell className="text-right tabular-nums">{rh.hours}h</TableCell><TableCell className="text-right tabular-nums font-semibold text-primary">{money(rh.pay)}</TableCell></TableRow>
                      ))}</TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </>
          ) : <div className="py-8 text-center text-muted-foreground">Loading…</div>}
        </DialogContent>
      </Dialog>

      {/* Confirm run */}
      <Dialog open={confirmRun} onOpenChange={setConfirmRun}>
        <DialogContent>
          <DialogHeader><DialogTitle>Run payroll?</DialogTitle>
            <DialogDescription>This creates a permanent static record of {rows.length} employees totaling {money(totalPay)} (paid through {through}), and locks those hours as paid. Time added afterward — even for earlier dates — automatically rolls into your next run.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmRun(false)}>Cancel</Button>
            <Button onClick={runPayroll} data-testid="confirm-run-btn">Confirm & Mark Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
